import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, getAdminToken, API, setAdminToken } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Users, Upload, Download, RefreshCw, Plus, Trash2, ArrowLeft, X, Search } from "lucide-react";

const ALL_TAGS = [
  "registration_5k", "registration_1mile", "vendor", "sponsor", "volunteer", "donor",
  "team_captain", "wellness_partner", "fuel_partner",
  "warm_up", "gym_team", "cheer_zone", "raffle", "wellness_table",
  "manual", "csv_import",
];
const SOURCES = ["registrations", "vendors", "sponsors", "volunteers", "donations", "manual", "csv_import"];

export default function AdminContacts() {
  const nav = useNavigate();
  const [authed] = useState(!!getAdminToken());
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newContact, setNewContact] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", tags: [], notes: "" });
  const fileRef = useRef(null);

  useEffect(() => {
    if (!authed) nav("/admin");
    else load();
    // eslint-disable-next-line
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (selectedTags.length) params.set("tags", selectedTags.join(","));
      if (selectedSources.length) params.set("sources", selectedSources.join(","));
      const { data } = await api.get(`/admin/contacts?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      if (err?.response?.status === 401) { setAdminToken(null); nav("/admin"); }
      else toast.error("Could not load contacts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (authed) load(); /* eslint-disable-next-line */ }, [selectedTags, selectedSources]);

  const toggle = (list, setter, val) => setter(list.includes(val) ? list.filter((x) => x !== val) : [...list, val]);

  const create = async (e) => {
    e.preventDefault();
    if (!newContact.email && !newContact.phone) { toast.error("Email or phone required."); return; }
    try {
      await api.post("/admin/contacts", newContact);
      toast.success("Contact added.");
      setNewContact({ first_name: "", last_name: "", email: "", phone: "", company: "", tags: [], notes: "" });
      setShowAdd(false);
      load();
    } catch { toast.error("Could not add contact."); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await api.delete(`/admin/contacts/${id}`);
      toast.success("Contact deleted.");
      load();
    } catch { toast.error("Delete failed."); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/contacts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported: ${data.added} added, ${data.updated} updated, ${data.skipped} skipped.`);
      load();
    } catch { toast.error("Upload failed."); }
    if (fileRef.current) fileRef.current.value = "";
  };

  const syncFromForms = async () => {
    try {
      const { data } = await api.post("/admin/contacts/sync-from-forms");
      toast.success(`Synced. Total contacts: ${data.total_contacts}`);
      load();
    } catch { toast.error("Sync failed."); }
  };

  const exportCsv = () => {
    const token = getAdminToken();
    fetch(`${API}/admin/contacts/export`, { headers: { "X-Admin-Token": token } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "contacts.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  const filterChips = useMemo(() => [...selectedTags, ...selectedSources], [selectedTags, selectedSources]);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <header className="border-b border-[#27272A] bg-[#0A0A0A] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Link to="/admin" className="text-[#71717A] hover:text-white flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> Dashboard</Link>
            <div className="ml-4">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#00A651] font-bold">Contacts</div>
              <div className="font-display text-lg uppercase text-white flex items-center gap-2"><Users className="w-5 h-5" /> {total.toLocaleString()} contacts</div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/admin/campaigns" data-testid="nav-campaigns" className="border border-white/20 hover:border-[#00A651] hover:text-[#00A651] text-white font-bold uppercase tracking-widest text-xs px-4 py-2">Campaigns →</Link>
            <button onClick={syncFromForms} data-testid="contacts-sync-btn" className="inline-flex items-center gap-2 border border-white/30 text-white hover:border-[#EC008C] hover:text-[#EC008C] font-bold uppercase tracking-widest text-xs px-4 py-2"><RefreshCw className="w-3.5 h-3.5" /> Sync from Forms</button>
            <button onClick={() => fileRef.current?.click()} data-testid="contacts-upload-btn" className="inline-flex items-center gap-2 border-2 border-[#EC008C] text-[#EC008C] hover:bg-[#EC008C] hover:text-white font-bold uppercase tracking-widest text-xs px-4 py-2"><Upload className="w-3.5 h-3.5" /> Import CSV</button>
            <input ref={fileRef} type="file" accept=".csv" onChange={upload} className="hidden" data-testid="contacts-csv-input" />
            <button onClick={exportCsv} data-testid="contacts-export-btn" className="inline-flex items-center gap-2 border border-white/30 text-white hover:border-[#00A651] hover:text-[#00A651] font-bold uppercase tracking-widest text-xs px-4 py-2"><Download className="w-3.5 h-3.5" /> Export</button>
            <button onClick={() => setShowAdd((v) => !v)} data-testid="contacts-add-btn" className="inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#1FBF3E] text-black font-bold uppercase tracking-widest text-xs px-4 py-2"><Plus className="w-3.5 h-3.5" /> Add Contact</button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {showAdd && (
          <form onSubmit={create} className="bg-[#141414] border border-[#00A651]/40 p-5 mb-6 grid md:grid-cols-3 gap-3">
            <Input data-testid="add-first-name" placeholder="First name" value={newContact.first_name} onChange={(e) => setNewContact({ ...newContact, first_name: e.target.value })} className="bg-black border-[#27272A]" />
            <Input data-testid="add-last-name" placeholder="Last name" value={newContact.last_name} onChange={(e) => setNewContact({ ...newContact, last_name: e.target.value })} className="bg-black border-[#27272A]" />
            <Input data-testid="add-company" placeholder="Company (optional)" value={newContact.company} onChange={(e) => setNewContact({ ...newContact, company: e.target.value })} className="bg-black border-[#27272A]" />
            <Input data-testid="add-email" type="email" placeholder="Email" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} className="bg-black border-[#27272A]" />
            <Input data-testid="add-phone" placeholder="Phone (+1...)" value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} className="bg-black border-[#27272A]" />
            <div className="md:col-span-3">
              <div className="text-[10px] uppercase tracking-[0.24em] text-[#A3A3A3] font-bold mb-2">Tags</div>
              <div className="flex flex-wrap gap-1.5">
                {ALL_TAGS.map((t) => (
                  <button key={t} type="button" onClick={() => setNewContact({ ...newContact, tags: newContact.tags.includes(t) ? newContact.tags.filter((x) => x !== t) : [...newContact.tags, t] })} className={`text-[10px] uppercase tracking-widest px-2.5 py-1 border ${newContact.tags.includes(t) ? "bg-[#00A651] text-black border-[#00A651]" : "border-[#27272A] text-[#A3A3A3] hover:border-white/40"}`}>{t}</button>
                ))}
              </div>
            </div>
            <Textarea placeholder="Notes (optional)" value={newContact.notes} onChange={(e) => setNewContact({ ...newContact, notes: e.target.value })} className="md:col-span-3 bg-black border-[#27272A]" rows={2} />
            <button type="submit" data-testid="add-submit-btn" className="md:col-span-3 bg-[#00A651] hover:bg-[#1FBF3E] text-black font-bold uppercase tracking-widest text-xs py-2.5">Save Contact</button>
          </form>
        )}

        {/* Filters */}
        <div className="bg-[#141414] border border-[#27272A] p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-3 md:items-center">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
              <Input data-testid="contacts-search" placeholder="Search name / email / phone / company" value={search} onKeyDown={(e) => e.key === "Enter" && load()} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-black border-[#27272A]" />
            </div>
            <button onClick={load} className="border border-white/30 hover:border-[#00A651] hover:text-[#00A651] font-bold uppercase tracking-widest text-xs px-4 py-2">Search</button>
          </div>
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#EC008C] font-bold mb-1.5">Filter by Tag</div>
            <div className="flex flex-wrap gap-1.5">
              {ALL_TAGS.map((t) => (
                <button key={t} onClick={() => toggle(selectedTags, setSelectedTags, t)} data-testid={`tag-filter-${t}`} className={`text-[10px] uppercase tracking-widest px-2.5 py-1 border transition-colors ${selectedTags.includes(t) ? "bg-[#EC008C] text-white border-[#EC008C]" : "border-[#27272A] text-[#A3A3A3] hover:border-white/40"}`}>{t}</button>
              ))}
            </div>
          </div>
          <div className="mt-3">
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#00A651] font-bold mb-1.5">Filter by Source</div>
            <div className="flex flex-wrap gap-1.5">
              {SOURCES.map((s) => (
                <button key={s} onClick={() => toggle(selectedSources, setSelectedSources, s)} data-testid={`source-filter-${s}`} className={`text-[10px] uppercase tracking-widest px-2.5 py-1 border transition-colors ${selectedSources.includes(s) ? "bg-[#00A651] text-black border-[#00A651]" : "border-[#27272A] text-[#A3A3A3] hover:border-white/40"}`}>{s}</button>
              ))}
            </div>
          </div>
          {filterChips.length > 0 && (
            <button onClick={() => { setSelectedTags([]); setSelectedSources([]); }} className="mt-3 text-[10px] uppercase tracking-widest text-[#71717A] hover:text-white flex items-center gap-1"><X className="w-3 h-3" /> Clear filters</button>
          )}
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-[#141414] border border-[#27272A]">
          {loading ? (
            <div className="p-10 text-center text-[#71717A] uppercase tracking-widest text-xs">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-[#71717A] uppercase tracking-widest text-xs">No contacts match your filters. Try &quot;Sync from Forms&quot; or &quot;Add Contact&quot;.</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-black text-[#A3A3A3] uppercase tracking-widest text-[10px]">
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Phone</th>
                  <th className="text-left px-4 py-3">Company</th>
                  <th className="text-left px-4 py-3">Tags</th>
                  <th className="text-left px-4 py-3">Source</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} data-testid={`contact-row-${c.id}`} className="border-t border-[#27272A] hover:bg-[#1A1A1A]">
                    <td className="px-4 py-3">{c.first_name} {c.last_name}</td>
                    <td className="px-4 py-3 text-[#E5E5E5]">{c.email || "—"}</td>
                    <td className="px-4 py-3 text-[#E5E5E5]">{c.phone || "—"}</td>
                    <td className="px-4 py-3">{c.company || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(c.tags || []).map((t) => <span key={t} className="text-[9px] uppercase tracking-widest bg-[#00A651]/20 text-[#00A651] px-1.5 py-0.5">{t}</span>)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-[10px] uppercase tracking-widest text-[#71717A]">{c.source}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => del(c.id)} data-testid={`contact-delete-${c.id}`} className="text-[#EC008C] hover:text-white p-1"><Trash2 className="w-4 h-4" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="mt-3 text-[10px] uppercase tracking-widest text-[#71717A]">Showing {items.length} of {total}. CSV format: first_name, last_name, email, phone, company, tags (semicolon-separated).</div>
      </main>
    </div>
  );
}
